import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Calendar, Send, Zap, Layers, BarChart3 } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { PAGE } from "@/lib/pageContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_OPTIONS = ["Roku", "Apple TV", "Fire TV", "Samsung", "LG", "Android TV", "iOS", "Android", "Web", "Set-Top Box"];

const PERK_ICONS = [Calendar, Layers, Zap, BarChart3];

export default function Demo() {
  const { lang, t } = useI18n();
  const p = pickLang(PAGE, lang);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", use_case: "" });
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (pl) => setPlatforms((s) => (s.includes(pl) ? s.filter((x) => x !== pl) : [...s, pl]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo-request`, { ...form, platforms });
      toast.success(res.data.message || "Demo request received!");
      setSent(true);
      setForm({ name: "", email: "", company: "", phone: "", use_case: "" });
      setPlatforms([]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="demo-page" className="relative overflow-hidden bg-radial-glow">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="overline">{p.demo_overline}</div>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] text-white">
              {p.demo_title_a} <br/>
              <span className="gradient-text">{p.demo_title_b}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-200 leading-relaxed">{p.demo_subtitle}</p>

            <div className="mt-10 md:mt-12 space-y-5">
              {p.demo_perks.map((perk, i) => {
                const Icon = PERK_ICONS[i] || Zap;
                return (
                  <div key={i} className="flex gap-4 items-start" data-testid={`demo-perk-${i}`}>
                    <div className="w-11 h-11 rounded-xl gradient-bg flex items-center justify-center shrink-0 text-black">
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-base md:text-lg text-white">{perk.title}</div>
                      <div className="text-slate-200 text-sm leading-relaxed">{perk.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right */}
          <div className="lg:col-span-7">
            <div className="glass rounded-3xl p-6 sm:p-8 md:p-10">
              {sent ? (
                <div data-testid="demo-success" className="text-center py-10 md:py-12">
                  <div className="w-16 h-16 rounded-full gradient-bg mx-auto flex items-center justify-center text-black mb-6">
                    <Send size={26} />
                  </div>
                  <h3 className="font-display text-3xl font-bold tracking-tight text-white">{p.demo_success_title}</h3>
                  <p className="mt-3 text-slate-200">{p.demo_success_sub}</p>
                  <button onClick={() => setSent(false)} data-testid="demo-submit-another" className="btn-ghost mt-8">
                    {p.demo_submit_another}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="demo-form" className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="form-label">{t("common.full_name")} *</label>
                      <input data-testid="demo-name-input" required type="text" className="form-input" placeholder="Jane Cooper" value={form.name} onChange={onChange("name")} />
                    </div>
                    <div>
                      <label className="form-label">{t("common.work_email")} *</label>
                      <input data-testid="demo-email-input" required type="email" className="form-input" placeholder="jane@company.com" value={form.email} onChange={onChange("email")} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="form-label">{t("common.company")} *</label>
                      <input data-testid="demo-company-input" required type="text" className="form-input" placeholder="Acme Streaming" value={form.company} onChange={onChange("company")} />
                    </div>
                    <div>
                      <label className="form-label">{t("common.phone")}</label>
                      <input data-testid="demo-phone-input" type="tel" className="form-input" placeholder="+1 (555) 000 0000" value={form.phone} onChange={onChange("phone")} />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">{p.demo_platforms_label}</label>
                    <div className="flex flex-wrap gap-2">
                      {PLATFORM_OPTIONS.map((pl) => {
                        const active = platforms.includes(pl);
                        return (
                          <button
                            type="button"
                            key={pl}
                            data-testid={`demo-platform-${pl.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => toggle(pl)}
                            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all border ${
                              active ? "gradient-bg text-black border-transparent" : "border-white/15 text-slate-100 hover:border-[#1FA8FF] hover:bg-white/[0.06]"
                            }`}
                          >
                            {active && <Check size={14} className="inline mr-1 -mt-0.5" />}
                            {pl}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="form-label">{p.demo_usecase_label}</label>
                    <textarea data-testid="demo-usecase-input" className="form-input" rows={5} placeholder={p.demo_usecase_placeholder} value={form.use_case} onChange={onChange("use_case")} />
                  </div>
                  <button data-testid="demo-submit-btn" type="submit" disabled={loading} className="btn-primary w-full justify-center">
                    {loading ? t("common.submitting") : <>{p.demo_submit} <ArrowRight size={18} /></>}
                  </button>
                  <p className="text-xs text-slate-300 text-center">{p.demo_disclaimer}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
